/**
 * ご契約情報画面
 * 契約者情報・プラン情報・支払い情報・工事情報など、契約に関する主要情報を一覧表示する。
 * 初回表示時にオプション情報も取得し、各オプション関連のRecoil stateを初期化する。
 * 戸建て・マンション・その他の契約種別によって表示するセクションを一部切り替える。
 */
import type { NextPage } from 'next';

import Heading from '@/components/atoms/Heading';
import ScrollLinkList from '@/components/molecules/ScrollLinkList';
import ConstructionInfo from '@/components/organisms/ConstructionInfo';
import CustomerInfo from '@/components/organisms/CustomerInfo';
import PaymentDetails from '@/components/organisms/PaymentDetails';
import PaymentInfo from '@/components/organisms/PaymentInfo';
import PlanInfo from '@/components/organisms/PlanInfo';
import SoNetMailInfo from '@/components/organisms/SoNetMailInfo';
import SonyAccountInfo from '@/components/organisms/SonyAccountInfo';
import { AnalyticsData } from '@/components/templates/AnalyticsData';
import BreadcrumbLists from '@/components/templates/BreadcrumbLists';
import alertState from '@/states/alertState';
import hikariTvOptionInfoState from '@/states/hikariTvOptionInfoState';
import { NuroDenkiOptionInfoState } from '@/states/nuroDenkiOptionInfoState';
import optionsInfoState from '@/states/optionsInfoState';
import PaymentDetailsStates from '@/states/paymentDetailsState';
import rvoipOptionInfoState from '@/states/rvoipOptionInfoState';
import { userState } from '@/states/selectors/userState';
import SoNetMailInfoState from '@/states/soNetMailInfoState';
import { unextOptionInfoState } from '@/states/unextOptionInfoState';
import voipOptionInfoState from '@/states/voipOptionInfoState';
import { OptionInfoResponse } from '@/types/api/mypage/options';
import api from '@/utils/api';
import { getSigV4HeadersForGet } from '@/utils/cognito';
import { ApiUrl, Constants } from '@/utils/constants';
import { AxiosResponse } from 'axios';
import { useEffect } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

const Contract: NextPage = () => {
  // 画面表示条件の分岐に使うユーザー情報
  const user = useRecoilValue(userState);

  // So-net メールシステム情報 state
  const soNetMailInfo = useRecoilValue(SoNetMailInfoState);

  // 公開用返金情報 state
  const paymentDetails = useRecoilValue(PaymentDetailsStates);
  const showPaymentDetails = paymentDetails.refund_list.length === 0;
  const setOptionsInfo = useSetRecoilState(optionsInfoState);
  const setAlert = useSetRecoilState(alertState);
  const setVoipOptionInfo = useSetRecoilState(voipOptionInfoState);
  const setRvoipOptionInfo = useSetRecoilState(rvoipOptionInfoState);
  const setHikariTv = useSetRecoilState(hikariTvOptionInfoState);
  const setUnext = useSetRecoilState(unextOptionInfoState);
  const setNuroDenki = useSetRecoilState(NuroDenkiOptionInfoState);

  // 契約情報画面で参照するオプション情報をマウント時に取得する
  useEffect(() => {
    const fetchOptions = async () => {
      const headers = await getSigV4HeadersForGet(ApiUrl.Read.OPTIONS_INFO);
      await api
        .get(ApiUrl.Read.OPTIONS_INFO, headers)
        .then((res: AxiosResponse<OptionInfoResponse>) => {
          const { data } = res;
          setOptionsInfo({
            option_inquiry_number: data.option_inquiry_number,
            options: data.options,
          });
          setVoipOptionInfo(data.voip);
          setRvoipOptionInfo(data.r_voip);
          setHikariTv(data.hikaritv);
          setUnext(data.unext);
          setNuroDenki(data.nurodenki);
        })
        .catch(() => {
          setAlert({
            isOpen: true,
            apiType: Constants.ApiType.API_GET,
          });
        });
    };

    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // マンションプランではSo-netメール情報を表示しない
  if (user.isMansion) {
    return (
      <>
        <AnalyticsData />
        <main className='l-main'>
          <Heading as='h1'>ご契約情報</Heading>
          <ScrollLinkList
            items={[
              { anchor: 'customer-info', title: 'お客さま情報' },
              { anchor: 'plan-info', title: 'ご契約内容' },
              { anchor: 'payment-info', title: 'お支払い情報' },
              { anchor: 'billing_construction_cost', title: '工事情報' },
              {
                anchor: 'payment-details',
                title: 'お支払い明細',
                hidden: showPaymentDetails,
              },
            ]}
          />
          {/** お客さま情報 */}
          <CustomerInfo anchor='customer-info' />

          {/** ご契約内容 */}
          <PlanInfo anchor='plan-info' />

          {/* お支払い情報 */}
          <PaymentInfo anchor='payment-info' />

          <ConstructionInfo anchor='billing_construction_cost' />

          {/* お支払い明細 */}
          <PaymentDetails anchor='payment-details' />

          {/* Sonyアカウント情報 */}
          <SonyAccountInfo />

          <BreadcrumbLists />
        </main>
      </>
    );
  }
  // 戸建てプランではSo-netメール情報の表示可否を追加で判定する
  if (user.isNuro) {
    return (
      <>
        <AnalyticsData />
        <main className='l-main'>
          <Heading as='h1'>ご契約情報</Heading>
          <ScrollLinkList
            items={[
              { anchor: 'customer-info', title: 'お客さま情報' },
              { anchor: 'plan-info', title: 'ご契約内容' },
              { anchor: 'payment-info', title: 'お支払い情報' },
              { anchor: 'billing_construction_cost', title: '工事情報' },
              {
                anchor: 'payment-details',
                title: 'お支払い明細',
                hidden: showPaymentDetails,
              },
              {
                anchor: 'sonet-mail',
                title: 'So-net メール情報',
                hidden:
                  !soNetMailInfo.mail_addr ||
                  soNetMailInfo.status === '1' ||
                  soNetMailInfo.status === '3',
              },
            ]}
          />
          {/** お客さま情報 */}
          <CustomerInfo anchor='customer-info' />

          {/** ご契約内容 */}
          <PlanInfo anchor='plan-info' />

          {/* お支払い情報 */}
          <PaymentInfo anchor='payment-info' />

          <ConstructionInfo anchor='billing_construction_cost' />

          {/* お支払い明細 */}
          <PaymentDetails anchor='payment-details' />

          {/* So-net メール情報 */}
          <SoNetMailInfo anchor='sonet-mail' />

          {/* Sonyアカウント情報 */}
          <SonyAccountInfo />

          <BreadcrumbLists />
        </main>
      </>
    );
  }
  return (
    <>
      <AnalyticsData />
      <main className='l-main'>
        <Heading as='h1'>ご契約情報</Heading>
        <ScrollLinkList
          items={[
            { anchor: 'customer-info', title: 'お客さま情報' },
            { anchor: 'plan-info', title: 'ご契約内容' },
            { anchor: 'payment-info', title: 'お支払い情報' },
            { anchor: 'billing_construction_cost', title: '工事情報' },
            {
              anchor: 'payment-details',
              title: 'お支払い明細',
              hidden: showPaymentDetails,
            },
          ]}
        />
        {/** お客さま情報 */}
        <CustomerInfo anchor='customer-info' />

        {/** ご契約内容 */}
        <PlanInfo anchor='plan-info' />

        {/* お支払い情報 */}
        <PaymentInfo anchor='payment-info' />

        <ConstructionInfo anchor='billing_construction_cost' />

        {/* お支払い明細 */}
        <PaymentDetails anchor='payment-details' />

        {/* Sonyアカウント情報 */}
        <SonyAccountInfo />

        <BreadcrumbLists />
      </main>
    </>
  );
};

export default Contract;
